import { SessionEstimate } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { recalcKey } from '@talon/web/src/context/stores/estimateStore';
import { USD } from '@talon/web/src/utils/currency';
import { formatDateOnly } from '@talon/web/src/utils/dateOnly';
import { isItemFieldEdited, itemDefaultFields } from '@shared/utils';
import { Box, DateInput, Keyboard, Menu, Text } from 'grommet';
import { More } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { ConfirmModal } from '../../ConfirmModal';
import { DeleteModal } from '../../DeleteModal';
import { EditableText } from '../../EditableText';
import { ContractSelect } from '../ContractSelect';
import { CostTier, FieldTier } from '../../CardTiers';
import { LabeledField } from '../../LabeledField';
import { StatusPill } from '../../StatusPill';
import { PremiumDayPill } from '../PremiumDayPill';

export interface ISessionEstimateHeaderProps {
  sessionEstimate: SessionEstimate;
  setSessionEstimate: (sessionEstimate: SessionEstimate) => void;
}

export const SessionEstimateHeader = observer(
  ({ sessionEstimate, setSessionEstimate }: ISessionEstimateHeaderProps) => {
    /** Context **/
    const estimateStore = useEstimateStore();

    /** State **/
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [title, setTitle] = useState(sessionEstimate.title);
    // Enter saves and then blur fires too, and blur used to save even when nothing changed - each
    // save recalculating the whole estimate. Only save a title that differs from the last one saved
    const lastSavedTitle = useRef(sessionEstimate.title);
    // The SessionEstimate's own date once one has been set here, otherwise its session's
    const [date, setDate] = useState(sessionEstimate.date ?? sessionEstimate.session?.date ?? '');

    /** Computed **/
    const hasEdits = !!sessionEstimate.items?.some((item) =>
      itemDefaultFields.some((field) => isItemFieldEdited(item, field))
    );
    const totalsBusy = estimateStore.isRecalculating(
      recalcKey.table('session', sessionEstimate.id),
      recalcKey.rows('session', sessionEstimate.id)
    );

    /** Methods **/
    // `typed` is the title just typed: saved straight away (Enter), its state update hasn't happened yet
    const saveTitle = (typed?: string) => {
      const next = typed ?? title;
      if (next === lastSavedTitle.current) return;
      lastSavedTitle.current = next;
      setSessionEstimate({ ...sessionEstimate, title: next });
    };

    const deleteSessionEstimate = async () => {
      await estimateStore.deleteSessionEstimate(sessionEstimate.id);
    };

    /** Render **/
    const canEdit = estimateStore.canEditCurrent;
    const menuItems = [{ label: 'Delete', onClick: () => setShowDeleteModal(true) }];
    if (hasEdits) menuItems.unshift({ label: 'Reset edits', onClick: () => setShowResetModal(true) });

    return (
      <Box>
        {/* Tier 1: the session estimate's settings */}
        <FieldTier>
          <LabeledField label="Session estimate" width={{ min: '14em' }} flex>
            {canEdit ? (
              <EditableText val={title} onChange={setTitle} onSubmit={saveTitle} onBlur={saveTitle} />
            ) : (
              <Text>{title}</Text>
            )}
          </LabeledField>
          <LabeledField label="Date">
            {canEdit ? (
              <Keyboard onEnter={() => setSessionEstimate({ ...sessionEstimate, date })}>
                <DateInput
                  format="mm/dd/yyyy"
                  value={date}
                  calendarProps={{ disabled: estimateStore.getSessionEstimateDates(sessionEstimate.id) }}
                  onChange={({ value }) => setDate(typeof value === 'object' ? value[0] : value)}
                />
              </Keyboard>
            ) : (
              <Text>{formatDateOnly(date)}</Text>
            )}
          </LabeledField>
          <LabeledField label="Contract">
            <ContractSelect
              hideLabel
              automaticBy="session date"
              appliedId={sessionEstimate.rate_schedule_id}
              overrideId={sessionEstimate.rate_schedule_override_id}
              onChange={(rateScheduleId) =>
                estimateStore.setSessionEstimateContract(sessionEstimate.id, rateScheduleId)
              }
            />
          </LabeledField>
          {sessionEstimate.session?.is_union && (
            <Box pad={{ bottom: '8px' }}>
              <StatusPill label="Union" />
            </Box>
          )}
          {/* Priced from the linked session's date; the estimate's own date is only a label */}
          <PremiumDayPill date={sessionEstimate.session?.date} />
          <Box flex />
          {canEdit && (
            <Menu
              icon={<More color="text-meta" />}
              a11yTitle={`More actions for ${title}`}
              dropAlign={{ top: 'bottom', right: 'right' }}
              dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
              items={menuItems}
            />
          )}
        </FieldTier>

        {/* Tier 2: what it costs */}
        <CostTier
          summary={hasEdits ? 'Some rates or fringe were edited by hand' : undefined}
          busy={totalsBusy}
          costs={[
            { label: 'Session wages', value: USD(sessionEstimate.subtotal) },
            { label: 'Session total', value: USD(sessionEstimate.subtotal_plus_fringe), emphasis: true },
          ]}
        />
        {showResetModal && (
          <ConfirmModal
            content="Set every rate and fringe edited by hand in this table back to the contract’s? Rows added by hand stay."
            confirmLabel="Reset edits"
            onConfirm={() => {
              setShowResetModal(false);
              estimateStore.resetSessionEstimateEdits(sessionEstimate.id);
            }}
            onCancel={() => setShowResetModal(false)}
          />
        )}
        {estimateStore.canEditCurrent && showDeleteModal && sessionEstimate && (
          <DeleteModal
            name={sessionEstimate.title}
            onDelete={deleteSessionEstimate}
            onCancel={() => setShowDeleteModal(false)}
          />
        )}
      </Box>
    );
  }
);
